import React, { useState } from 'react';
import { useToast } from '../ToastManager';
import DataTable from '../ui/DataTable';
import ConfirmDialog from '../ui/ConfirmDialog';
import StatusBadge from '../ui/StatusBadge';
import ChannelBadge from '../ui/ChannelBadge';
import OrderInspectorDrawer, { STATUS_SELECCIONABLES } from './OrderInspectorDrawer';
import {
  statusDef, normalizeSalesChannel, fmtMoney,
  getPaymentLabel, getOrderOrigin, formatOrderDate,
} from '../../utils/orderUtils';
import { decodeHtml } from '../../utils/decodeHtml';
import { MENSAJE_SOLO_LECTURA } from '../../utils/soloLectura';
import { OrderItems, OrderTotals, customerName, hasPendingShipping } from './orderInspectorShared';
import '../../pages/OrdersPage.css'; // reusa las clases op-* (F5 Tema 3)

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/* ── Superficie compartida de pedidos (F5 Tema 3) ────────────────────────────
   Lista (DataTable) + drawer (EntityInspector) + cancelar (ConfirmDialog),
   extraída VERBATIM de OrdersPage para que Pedidos Y Resumen muestren el MISMO
   objeto con el mismo componente (principio rector: una entidad migrada, un solo
   componente). CONTROLADO para `selected` (el padre lo posee — no romper el
   deep-link del buscador global). El cancelar hace la API y avisa al padre con
   `onOrderCancelled(id)` para que actualice su lista y su `selected`. */

// Helpers compartidos → módulo propio (F5#4, orderInspectorShared); se RE-EXPORTAN
// para no romper a quien ya los importa desde acá (OrderEditPage, drawers de envío,
// OrdersPage). Cero cambio para los consumidores.
export { OrderItems, OrderTotals, customerName, hasPendingShipping };

/* Fecha corta para la celda (hallazgo 1 F3.1: sin año — el rango activo ya lo
   acota; la fecha completa vive en la expandida y el inspector) */
function fmtFechaCorta(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return `${d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit' })} · ${d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return dateStr;
  }
}

export default function OrderListInspector({
  orders,
  selected,
  onSelect,
  loading = false,
  error = null,
  onRetry,
  pagination,
  empty,
  hasActiveFilters = false,
  onClearFilters,
  isVendedor = false,
  soloLectura = false,
  onEdit,
  onManageShipping,
  onOrderCancelled,
  onOrderStatusChanged,
  onContactoGuardado,
}) {
  const { showToast } = useToast();
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  /* F3.5 (dos superficies): abrir la edición cierra el inspector (nunca dos capas). */
  const abrirEdicion = (o) => {
    onSelect?.(null);
    onEdit?.(o);
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/${cancelTarget.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'No se pudo cancelar el pedido.');
      }
      // Un 200 NO alcanza: el backend responde con el estado que confirmó
      // WooCommerce. Si no volvió 'cancelled', el pedido sigue vivo y pintarlo
      // como cancelado sería mentirle al usuario (fallo silencioso).
      if (data.status !== 'cancelled') {
        throw new Error(
          data.status
            ? `El pedido NO se canceló: sigue en "${data.status}". Actualiza y vuelve a intentar.`
            : 'No se pudo confirmar la cancelación. Actualiza la lista y verifica el estado del pedido.'
        );
      }
      showToast({ message: 'Pedido cancelado.', variant: 'success' });
      // El padre posee orders + selected: actualiza ambos a 'cancelled'.
      onOrderCancelled?.(cancelTarget.id);
      setCancelTarget(null);
    } catch (err) {
      showToast({ message: err.message, variant: 'error' });
    } finally {
      setCancelling(false);
    }
  };

  // Cambio de estado NO-destructivo (el selector del drawer; cancelled/refunded NO pasan por acá,
  // siguen por handleCancel con confirmación). Mismo contrato que el cancel: un 200 no alcanza,
  // se exige que WooCommerce haya confirmado el estado pedido. El backend ya limpia la caché de
  // /api/orders en este endpoint, así que la lista refleja el cambio sin esperar el TTL.
  const handleChangeStatus = async (order, newStatus) => {
    if (!order || changingStatus || newStatus === order.status) return;
    if (!STATUS_SELECCIONABLES.includes(newStatus)) return; // el drawer solo ofrece los no-destructivos
    setChangingStatus(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'No se pudo cambiar el estado del pedido.');
      if (data.status !== newStatus) {
        throw new Error(data.status
          ? `El estado no cambió: sigue en "${statusDef(data.status).label}". Actualiza y vuelve a intentar.`
          : 'No se pudo confirmar el cambio. Actualiza la lista y verifica el estado del pedido.');
      }
      showToast({ message: `Pedido #${order.number} → ${statusDef(newStatus).label}.`, variant: 'success' });
      onOrderStatusChanged?.(order.id, newStatus);
    } catch (err) {
      showToast({ message: err.message, variant: 'error' });
    } finally {
      setChangingStatus(false);
    }
  };

  const columns = [
    { id: 'number',  header: 'Pedido', render: (o) => (
      <span className="op-cell-num">
        <strong>#{o.number}</strong>
        {(o.shipping_lines?.length > 0) && (
          <span
            className={`op-ship-ico${hasPendingShipping(o) ? ' op-ship-ico--pend' : ''}`}
            title={hasPendingShipping(o) ? 'Envío pendiente' : 'Enviado'}
            aria-label={hasPendingShipping(o) ? 'Envío pendiente' : 'Enviado'}
          >📦</span>
        )}
      </span>
    ) },
    /* hideSm (pasada móvil F3): en el teléfono la fila se queda con lo esencial
       (pedido+📦, cliente, total, estado) — fecha, productos y canal siguen en
       la fila expandida y en el inspector. */
    { id: 'fecha',   header: 'Fecha', hideSm: true, render: (o) => <span className="op-cell-nowrap">{fmtFechaCorta(o.date_created)}</span> },
    { id: 'cliente', header: 'Cliente', render: (o) => {
      const n = customerName(o);
      return <span className="op-cell-clip" title={n}>{n}</span>;
    } },
    { id: 'items',   header: 'Productos', hideSm: true, render: (o) => {
      const items = o.line_items || [];
      if (!items.length) return '—';
      const first = items[0];
      const vars = (first.meta_data || [])
        .filter((m) => !m.key?.startsWith('_') && m.display_value && m.display_key)
        .map((m) => decodeHtml(m.display_value)).join(' / ');
      const extra = items.length - 1;
      const unidades = items.reduce((s, it) => s + (it.quantity || 0), 0);
      const main = vars ? `${first.name} - ${vars}` : first.name;
      const fullList = items.map((it) => `${it.name} ×${it.quantity}`).join(' · ');
      return (
        <div className="op-cell-items" title={fullList}>
          <span className="op-cell-items-main">{main}</span>
          <span className="op-cell-items-sub">{extra > 0 ? `+${extra} más · ` : ''}{unidades} u.</span>
        </div>
      );
    } },
    { id: 'total',   header: 'Total', align: 'right', render: (o) => fmtMoney(parseFloat(o.total || 0)) },
    /* La columna "Pago" salió (hallazgo 1: baja señal en la fila — el método
       vive en la expandida y el inspector; el filtro sigue en "Filtros") */
    { id: 'estado',  header: 'Estado', render: (o) => { const d = statusDef(o.status); return <StatusBadge variant={d.variant}>{d.label}</StatusBadge>; } },
    { id: 'canal',   header: 'Canal', hideSm: true, render: (o) => <ChannelBadge channel={normalizeSalesChannel(o).key} /> },
  ];

  // F7: en una tienda de solo lectura, las acciones que ESCRIBEN se ofrecen DESHABILITADAS con la
  // explicación (no se ocultan: el usuario ve por qué no puede), y su onClick queda inerte.
  const sl = soloLectura ? { disabled: true, title: MENSAJE_SOLO_LECTURA, onClick: () => {} } : {};
  const rowActions = (o) => [
    ...(!isVendedor ? [{ id: 'editar', label: 'Editar pedido', onClick: () => abrirEdicion(o), ...sl }] : []),
    ...(hasPendingShipping(o) ? [{ id: 'envio', label: 'Gestionar envío', onClick: () => onManageShipping?.(o), ...sl }] : []),
    ...(!isVendedor && o.status !== 'cancelled' && o.status !== 'refunded'
      ? [{ id: 'cancelar', label: 'Cancelar pedido', destructive: true, onClick: () => setCancelTarget(o), ...sl }] : []),
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={orders}
        rowKey="id"
        loading={loading}
        error={error}
        onRetry={onRetry}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
        empty={empty}
        onRowClick={(o) => onSelect?.(o)}
        selectedKey={selected?.id ?? null}
        rowActions={rowActions}
        rowActionsHideSm
        renderExpanded={(o) => (
          <div className="op-row__body">
            <div className="op-row__section">
              <p className="op-section-label">Productos</p>
              <OrderItems order={o} />
            </div>
            <OrderTotals order={o} />
            <div className="op-row__meta">
              <div className="op-meta-item"><span className="op-meta-label">Pago</span><span>{getPaymentLabel(o).label}</span></div>
              <div className="op-meta-item"><span className="op-meta-label">Canal</span><span>{getOrderOrigin(o).label}</span></div>
              <div className="op-meta-item"><span className="op-meta-label">Fecha exacta</span><span>{formatOrderDate(o.date_created)}</span></div>
              {(o.shipping?.address_1 || o.billing?.address_1) && (
                <div className="op-meta-item"><span className="op-meta-label">Dirección</span>
                  <span>{[o.shipping?.address_1 || o.billing?.address_1, o.shipping?.city || o.billing?.city].filter(Boolean).join(', ')}</span>
                </div>
              )}
              {o.customer_note && (
                <div className="op-meta-item op-meta-item--wide"><span className="op-meta-label">Notas del cliente</span><span>{o.customer_note}</span></div>
              )}
            </div>
          </div>
        )}
        pagination={pagination}
      />

      {/* Drawer del pedido — EXTRAÍDO a OrderInspectorDrawer (F5#4) para compartirlo
          con Caja. Verbatim: Pedidos y Resumen se comportan idéntico. El
          ConfirmDialog de cancelación se queda acá (lo comparte con el ⋮ de la fila);
          el botón del drawer lo dispara vía onRequestCancel. */}
      <OrderInspectorDrawer
        selected={selected}
        orders={orders}
        onSelect={onSelect}
        isVendedor={isVendedor}
        soloLectura={soloLectura}
        onEdit={onEdit}
        onManageShipping={onManageShipping}
        onRequestCancel={setCancelTarget}
        onChangeStatus={!isVendedor ? handleChangeStatus : null}
        onContactoGuardado={onContactoGuardado}
      />

      {/* Confirmación de cancelación (conserva el texto con consecuencias) */}
      <ConfirmDialog
        open={!!cancelTarget}
        title={cancelTarget ? `¿Cancelar pedido #${cancelTarget.number}?` : ''}
        confirmLabel={cancelling ? 'Cancelando…' : 'Sí, cancelar pedido'}
        cancelLabel="Volver"
        loading={cancelling}
        onConfirm={handleCancel}
        onCancel={() => setCancelTarget(null)}
      >
        Esta acción actualiza el estado en WooCommerce. El stock se puede revertir manualmente desde el pedido.
      </ConfirmDialog>
    </>
  );
}
